/* eslint-disable react/no-array-index-key */
import {
  Fragment,
  HTMLAttributes,
  lazy,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import { useTranslate } from '@tolgee/react';
import { Button, message, Space, Badge, Tooltip, Tag } from 'antd';
import {
  CaretDownOutlined,
  CaretUpOutlined,
  ReloadOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import dayjs from 'lib/dayjs';
import { map, noop } from 'lodash-es';
import useBigVisible from 'hooks/useBigFold';
import { updateTradeComment, syncOrderReview } from 'services/api/conversation';
import forwardSvg from 'assets/icons/forward.svg';
import SellerCenter from 'components/SellerCenter';
import { buyerNickMask } from 'services/channels/demo';
import ContactWhatsapp from 'components/ContactWhatsapp';
import Copy from './Copy';
import { DetailItem } from './TradeDetailPaymentLazada';
import TradeCommentForm, { TradeCommentFormValues } from './TradeCommentForm';
import TradeProduct from './TradeProduct';
import TradeCancelInfo from './TradeCancelInfo';
import TradeBuyerMemos from './TradeBuyerMemos';
import TradeDetail from './TradeDetail';

import { TradeContext } from './StateContext';
import { Between } from './TradeRefund';
import { LinkButton } from './Head';
import TradeHead from './TradeHead';
import TradeStatus from './TradeStatus';
import { tradeDetailFirstTipCache } from 'services/cache';
import TradeMemoRecord from './TradeMemoRecord';
import { useAuthorization } from 'components/Authorization';
import { ProductTagList, TagProduct } from 'beisi/TagProduct';
import { QIFeature } from 'utils/featureFlag';
import { useTFERPEnable } from 'hooks/useTFERPEnable';
import { useTFERPVersionValue } from 'hooks/useTFERPVersion';
import { ExchangeButton } from 'yphh/Exchange';
import { useIsYPHH } from 'yphh/feature';
import { ExportOrderButton } from './ExportOrder';
import { useTradeListContext } from './TradeContext';
import { useRequest } from 'ahooks';
import { BookingTag } from './TradeComponentDetail';
import StatusButton from 'components/StatusButton';
import { getAppProductName } from 'hooks/useTrans';
import { TradeProductsRefund } from './TradeProductsRefund';
import { getOrderSellerCenter } from './utils';
import { useOrderSendButtonInfo } from 'hooks/business/chat/useOrder';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import { sellerMemosSupportOfficialPlatforms } from './constants';
import { useSendReturnCard, UseSendReturnCardProps } from './hooks/useSendReturnCard';
import CancelOrder from './TiktokProactive/CancelOrder';
import Refund from './TiktokProactive/Refund';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';

const TFERPPage = lazy(() => import('./TFERPPage'));
const YiPinErpDetail = lazy(() => import('./YiPinErpDetail'));

export interface TradeComponentProps extends HTMLAttributes<HTMLDivElement> {
  trade: Conversation.Trade.TradeWithLogistics;
  conversation: Conversation.Conversation;
  onUpdate: (trade: Conversation.Trade.TradeWithLogistics) => void;
  sendable?: boolean;
  setCurTrade?: React.Dispatch<
    React.SetStateAction<Conversation.Trade.TradeWithLogistics | undefined>
  >;
  showDetail?: string;
  setShowDetail?: React.Dispatch<React.SetStateAction<string>>;
  showBreath?: boolean;
  onOpenDetail?: (trade: Conversation.Trade.TradeWithLogistics) => void;
  reviewId?: string;
  lazyScrollContainer?: Element | null;
  onScrollTo?: (id: string) => Promise<any>;
}

/**
 * 乐聊订单备注
 */
function TradeCommentContent({
  trade,
  conversation,
  onEdit,
}: {
  trade: Conversation.Trade.TradeWithLogistics;
  conversation: Conversation.Conversation;
  onEdit: () => void;
}) {
  const { t } = useTranslate();
  const { lechatSellerMemos, sellerMemosOperationLog, tradeStatus } = trade;

  const { channel, controlStatus } = conversation;

  return (
    <MemoDetail>
      <div>
        <span>
          {t('scene.chatWorkspace.orderTab.notes.title')}
          {sellerMemosSupportOfficialPlatforms.includes(channel) && `(${getAppProductName()})`}
        </span>
        {tradeStatus !== 'CANCEL' && channel !== 'TOKOPEDIA' && (
          <div>
            {Boolean(sellerMemosOperationLog?.length) && (
              <TradeMemoRecord sellerMemosOperationLog={sellerMemosOperationLog} />
            )}
            {!QIFeature && (
              <Button type="link" disabled={!controlStatus} onClick={onEdit}>
                {lechatSellerMemos
                  ? t('scene.chatWorkspace.orderTab.notes.action.edit')
                  : t('scene.chatWorkspace.orderTab.notes.action.add')}
              </Button>
            )}
          </div>
        )}
      </div>
      {lechatSellerMemos ? <span>{lechatSellerMemos}</span> : null}
    </MemoDetail>
  );
}
/**
 * 电商平台官方订单备注
 */
function TradeOfficialComment({
  isShopeeIn30daysMemo,
  trade,
  conversation,
  onEdit,
}: {
  isShopeeIn30daysMemo: boolean;
  trade: Conversation.Trade.TradeWithLogistics;
  conversation: Conversation.Conversation;
  onEdit: () => void;
}) {
  const { t } = useTranslate();
  const { sellerMemosOperationLog, tradeStatus, sellerMemos } = trade;

  const { channel, controlStatus } = conversation;

  if (!sellerMemosSupportOfficialPlatforms.includes(channel)) {
    return null;
  }

  if (channel === 'SHOPEE') {
    if (isShopeeIn30daysMemo) {
      return (
        <MemoDetail>
          <div>
            <span>{t('scene.chatWorkspace.orderTab.notes.title')}</span>
            {tradeStatus !== 'CANCEL' && (
              <div>
                {Boolean(sellerMemosOperationLog?.length) && (
                  <TradeMemoRecord sellerMemosOperationLog={sellerMemosOperationLog} />
                )}
                {!QIFeature && (
                  <Button type="link" disabled={!controlStatus} onClick={onEdit}>
                    {sellerMemos
                      ? t('scene.chatWorkspace.orderTab.notes.action.edit')
                      : t('scene.chatWorkspace.orderTab.notes.action.add')}
                  </Button>
                )}
              </div>
            )}
          </div>
          {sellerMemos ? <span>{sellerMemos}</span> : null}
        </MemoDetail>
      );
    }

    if (sellerMemos) {
      return (
        <MemoDetail>
          <div>
            <span>{t('scene.chatWorkspace.orderTab.notes.title')}</span>
            <div>
              <Tooltip title={t('scene.chatWorkspace.orderTab.note.unmodifiableDueToShopeePolicy')}>
                <Button disabled type="link">
                  {t('scene.chatWorkspace.orderTab.notes.action.edit')}
                </Button>
              </Tooltip>
            </div>
          </div>
          <span>{sellerMemos}</span>
        </MemoDetail>
      );
    }

    return null;
  }

  return sellerMemos ? (
    <OfficialMemo>
      <div>{t('scene.chatWorkspace.orderTab.notes.title')}</div>
      <div title="sellerMemos">{sellerMemos}</div>
    </OfficialMemo>
  ) : null;
}

const SendCardButton = (props: UseSendReturnCardProps) => {
  const { enable, sendReturnCard } = useSendReturnCard(props);

  const { t } = useTranslate();

  if (!enable) {
    return null;
  }

  return (
    <div style={{ textAlign: 'right' }}>
      <StatusButton
        type="link"
        size="small"
        style={{ fontSize: 12, paddingRight: 0 }}
        onClick={sendReturnCard}
      >
        {t('scene.chatWorkspace.orderTab.returnRefund.action.sendReturnCard')}
      </StatusButton>
    </div>
  );
};

function TradeProducts({
  trade,
  itemsData,
  conversation,
  lazyScrollContainer,
  onUpdate,
}: {
  trade: Conversation.Trade.TradeWithLogistics;
  itemsData: Conversation.Trade.ReturnProductsData;
  conversation: Conversation.Conversation;
  lazyScrollContainer?: Element | null;
  onUpdate: TradeComponentProps['onUpdate'];
}) {
  const { t } = useTranslate();
  const itemsVisible = useBigVisible();

  const products = itemsData.products;

  if (products.length === 0) {
    return null;
  }

  const { conversationId, channel } = conversation;
  const { externalTradeId, externalShopId, tradeStatus, logisticsList, cancelType, cancelReason } =
    trade;

  const {
    visible: isItemsUnfold,
    loading: itemsLoading,
    show: showItems,
    hide: hideItems,
  } = itemsVisible;

  return (
    <div>
      {(isItemsUnfold ? products : products.slice(0, 2)).map((item, index) => {
        const { itemId, itemSku: sku, externalSkuId } = item;

        return (
          <Fragment key={`${itemId}-${sku}-${index}`}>
            <Product
              product={item}
              conversation={conversation}
              productIndex={index}
              externalShopId={externalShopId}
              showReturn
              lazyScrollContainer={lazyScrollContainer}
            />
            <ProductTagList trade={trade} productData={item} />
            <SendCardButton
              conversationId={conversationId}
              channel={channel}
              tradeStatus={tradeStatus}
              leChatStatus={logisticsList?.[0]?.leChatStatus}
              cancelType={cancelType}
              cancelReason={cancelReason}
              externalTradeId={externalTradeId}
              externalSkuId={externalSkuId}
            />
          </Fragment>
        );
      })}
      {products.length > 2 && (
        <Button
          block
          loading={itemsLoading}
          type="link"
          onClick={() => (isItemsUnfold ? hideItems() : showItems())}
        >
          {isItemsUnfold
            ? t('scene.chatWorkspace.orderTab.action.collapse')
            : t('scene.chatWorkspace.orderTab.action.expand')}
          {isItemsUnfold ? <CaretUpOutlined /> : <CaretDownOutlined />}
        </Button>
      )}
      <ExchangeButton
        conversation={conversation}
        products={products}
        trade={trade}
        onSuccess={(newTrade) => {
          onUpdate(newTrade);
        }}
      />
    </div>
  );
}

function TradeComponent(props: TradeComponentProps) {
  const {
    trade,
    conversation,
    onUpdate,
    sendable = true,
    showDetail,
    setShowDetail,
    setCurTrade,
    showBreath = false,
    onOpenDetail,
    reviewId,
    lazyScrollContainer,
    onScrollTo,
    ...otherProps
  } = props;

  const {
    internalTradeId: id,
    externalTradeId,
    invoiceNumber,
    createdTimeInMs: createTime,
    sellerMemos,
    lechatSellerMemos,
    items,
    paymentMethod,
    phone,
    email,
    sellerMemosOperationLog,
    matchBooking,
    bookingId,
    taxIdType,
    taxIdNumber,
    tradeStatus,
    editSellerMemoType,
    packId,
    eligibility,
    replacedOrderId,
    exchangeSourceOrderId,
    generatedOrders,
  } = trade;

  const { refreshTrade } = useTradeListContext();

  const { channel, storeId } = conversation;

  const auth = useAuthorization<Oversea.Token.Payload>();
  const displaySensitive = useGetPermission()('订单敏感信息');

  const isYPHH = useIsYPHH();

  const isShopeeIn30daysMemo = channel === 'SHOPEE' && editSellerMemoType !== 'LECHAT';

  const formInitialValues = {
    internalTradeId: id,
    sellerMemos: isShopeeIn30daysMemo ? sellerMemos : lechatSellerMemos,
    platform: channel,
    editSellerMemoType,
  };

  const [editing, setEditing] = useState(false);

  const { t } = useTranslate();

  const tfERPEnable = useTFERPEnable();
  const tferpVersion = useTFERPVersionValue(tfERPEnable);
  const isTFERPSupport =
    channel === 'TIKTOK' || channel === 'TIKTOK_US' ? tferpVersion === 'V3' : tfERPEnable;

  useEffect(() => {
    if (showDetail === trade.internalTradeId && setCurTrade) {
      setCurTrade(trade);
    }
  }, [showDetail, trade, setCurTrade]);

  const commentContentDisabled =
    isShopeeIn30daysMemo || (tradeStatus === 'CANCEL' && !lechatSellerMemos);
  const showCommentContent = !commentContentDisabled && !editing;

  /** 备注编辑提交 */
  const handleSubmit = useCallback(
    (values: TradeCommentFormValues) => {
      return updateTradeComment(values)
        .then(() => {
          message.success(t('scene.chatWorkspace.orderTab.notes.feedback.updateSuccess'));
          batchedUpdates(() => {
            setEditing(false);
            const { platform, sellerMemos, ...others } = values;

            const nextSellerMemosOperationLog = [
              ...(sellerMemosOperationLog || []),
              {
                userName: auth?.payload?.name || '',
                operateTimeMs: `${Date.now()}`,
                raw: JSON.stringify({ sellerMemos }),
              },
            ];

            onUpdate({
              ...trade,
              ...others,
              [isShopeeIn30daysMemo ? 'sellerMemos' : 'lechatSellerMemos']: sellerMemos,
              sellerMemosOperationLog: nextSellerMemosOperationLog,
            });
          });
        })
        .catch(() => {
          message.error(t('scene.chatWorkspace.orderTab.notes.feedback.updateFailed'));
        });
    },
    [trade, onUpdate, t, sellerMemosOperationLog, auth, isShopeeIn30daysMemo],
  );

  /**
   * 从订单商品列表分类退单商品和其他商品
   * returnSnObj: 退单商品(map) 退款号 -> 商品数组
   * products: 其他商品(array) 商品数组
   */
  const itemsData = useMemo(() => {
    return items.reduce(
      (prev, cur: Conversation.Trade.Product, index) => {
        if (cur.refundReason?.length) {
          cur.refundReason.forEach((r) => {
            const { returnSn, returnTrackingNumber } = r;
            if (returnSn) {
              if (Array.isArray(prev.returnSnObj[returnSn])) {
                prev.returnSnObj[returnSn].push({ ...cur, returnTrackingNumber, index });
              } else {
                // eslint-disable-next-line no-param-reassign
                prev.returnSnObj[returnSn] = [{ ...cur, returnTrackingNumber, index }];
              }
            }
          });
        } else {
          prev.products.push(cur);
        }
        return prev;
      },
      {
        returnSnObj: {},
        products: [],
      } as Conversation.Trade.ReturnProductsData,
    );
  }, [items]);

  const products = (
    <TradeProducts
      trade={trade}
      itemsData={itemsData}
      conversation={conversation}
      lazyScrollContainer={lazyScrollContainer}
      onUpdate={onUpdate}
    />
  );

  // 是否支持发送订单
  const {
    sendEnabled: supportSend,
    sendDisabled,
    sendHandler,
  } = useOrderSendButtonInfo(trade, conversation, sendable);

  const tagProducts = (
    <TagProduct
      productData={itemsData.products}
      trade={trade}
      conversation={conversation}
      sourceId={reviewId}
    />
  );

  const BtnDetail = setShowDetail ? (
    <Button
      size="small"
      type="default"
      onClick={() => {
        setShowDetail(trade.internalTradeId);
        tradeDetailFirstTipCache.set('1');
        onOpenDetail?.(trade);
      }}
      disabled={channel === 'TOKOPEDIA'}
    >
      {t('scene.chatWorkspace.orderTab.action.viewDetail')}
    </Button>
  ) : null;
  let BtnDetailWrap = BtnDetail;
  if (BtnDetail && showBreath && channel !== 'TOKOPEDIA') {
    BtnDetailWrap = <DetailBadge dot>{BtnDetail}</DetailBadge>;
  }

  const displayTradeId = channel === 'TOKOPEDIA' ? invoiceNumber : externalTradeId;
  const { runAsync: syncShopeeReview, loading: syncLoading } = useRequest(
    () =>
      syncOrderReview({
        platform: trade.platform,
        orderId: externalTradeId,
        spuId: map(items, (each) => each.itemId),
      }).then(() => {
        return refreshTrade(
          {
            page_no: 1,
            page_size: 1,
            external_buyer_id: conversation.externalBuyerId,
            internal_store_id: conversation.storeId,
            external_trade_ids: externalTradeId,
          },
          trade.platform,
        );
      }),
    {
      manual: true,
      onError: () => {
        message.error(t('scene.chatWorkspace.orderTab.error.operationFailed'));
      },
    },
  );

  return (
    <Container {...otherProps}>
      {conversation.channel === 'WA_BIZ' ? <TradeHead trade={trade} /> : null}
      <Top>
        <div>
          <TradeStatus trade={trade} />
          <span>{dayjs(Number(createTime)).format('YYYY-MM-DD, HH:mm')}</span>
        </div>
        <Between>
          <Space style={{ marginRight: '8px' }}>
            <div style={{ color: '#666' }}>{displayTradeId}</div>
            {paymentMethod ? (
              <SpanCOD title={paymentMethod}>
                [<b>{paymentMethod}</b>]
              </SpanCOD>
            ) : null}
            <Copy
              text={displayTradeId}
              successMessage={t('common.feedback.copySuccess')}
              failedMessage={t('common.feedback.copyFailed')}
            />
          </Space>
          {channel === 'SHOPEE' && (
            <Tooltip
              title={t('scene.chatWorkspace.reviewTab.action.refreshData')}
              placement="topRight"
            >
              {syncLoading ? (
                <LoadingOutlined />
              ) : (
                <ReloadOutlined
                  onClick={() => {
                    syncShopeeReview();
                  }}
                />
              )}
            </Tooltip>
          )}
          {channel === 'LAZADA' && (
            <SellerCenter platform={channel}>
              <LinkButton
                onClick={() => {
                  getOrderSellerCenter(storeId, externalTradeId).catch(() => {
                    message.error(t('scene.chatWorkspace.orderTab.error.openSellerCenterFailed'));
                  });
                }}
              >
                <div
                  style={{
                    whiteSpace: 'nowrap',
                    maxWidth: '100px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {t('scene.chatWorkspace.orderTab.action.viewSellerCenter')}
                </div>
                <img src={forwardSvg} alt="forward" />
              </LinkButton>
            </SellerCenter>
          )}
        </Between>
        {taxIdNumber ? (
          <div>
            <Space>
              <span title={taxIdNumber}>
                [{taxIdType} {taxIdNumber}]
              </span>
              <Copy
                text={taxIdNumber}
                successMessage={t('common.feedback.copySuccess')}
                failedMessage={t('common.feedback.copyFailed')}
              />
            </Space>
          </div>
        ) : null}
        {packId ? (
          <div>
            <Space>
              <span title={packId}>[Pack ID: {packId}]</span>
              <Copy
                text={packId}
                successMessage={t('common.feedback.copySuccess')}
                failedMessage={t('common.feedback.copyFailed')}
              />
            </Space>
          </div>
        ) : null}
        {matchBooking && (
          <div>
            <Space>
              {bookingId
                ? [
                    `BookingID:${bookingId}`,
                    <Copy
                      text={bookingId}
                      successMessage={t('common.feedback.copySuccess')}
                      failedMessage={t('common.feedback.copyFailed')}
                    />,
                  ]
                : null}
              <BookingTag>{t('scene.chatWorkspace.orderTab.label.matchedOrder')}</BookingTag>
            </Space>
          </div>
        )}
        {exchangeSourceOrderId ? (
          <div className="!justify-start" style={{ fontSize: 12 }}>
            <Tag color="blue" style={{ color: '#096dd9', border: 'none', fontSize: 12 }}>
              {t('换货订单')}
            </Tag>
            <span>
              {t('来源订单')}: {exchangeSourceOrderId}
            </span>
            {onScrollTo && (
              <StatusButton
                style={{ fontSize: 12 }}
                type="link"
                onClick={() => onScrollTo(exchangeSourceOrderId)}
              >
                {t('aide.查看')} →
              </StatusButton>
            )}
          </div>
        ) : null}
        {replacedOrderId ? (
          <div className="!justify-start" style={{ fontSize: 12 }}>
            <Tag color="blue" style={{ color: '#096dd9', border: 'none', fontSize: 12 }}>
              {t('补发订单')}
            </Tag>
            <span>
              {t('来源订单')}: {replacedOrderId}
            </span>
            {onScrollTo && (
              <StatusButton
                style={{ fontSize: 12 }}
                type="link"
                onClick={() => onScrollTo(replacedOrderId)}
              >
                {t('aide.查看')} →
              </StatusButton>
            )}
          </div>
        ) : null}
        {generatedOrders?.map(({ orderTag, orderId }) => {
          if (orderTag === 'EXCHANGE_ORDER') {
            return (
              <div className="!justify-start" style={{ fontSize: 12 }}>
                <span>
                  {t('已生成换货订单')}: {orderId}
                </span>
                {onScrollTo && (
                  <StatusButton
                    style={{ fontSize: 12 }}
                    type="link"
                    onClick={() => onScrollTo(orderId)}
                  >
                    {t('aide.查看')} →
                  </StatusButton>
                )}
              </div>
            );
          }
          if (orderTag === 'REPLACEMENT_ORDER') {
            return (
              <div className="!justify-start" style={{ fontSize: 12 }}>
                <span>
                  {t('已生成补发订单')}: {orderId}
                </span>
                {onScrollTo && (
                  <StatusButton
                    style={{ fontSize: 12 }}
                    type="link"
                    onClick={() => onScrollTo(orderId)}
                  >
                    {t('aide.查看')} →
                  </StatusButton>
                )}
              </div>
            );
          }
          return null;
        })}
      </Top>
      <TradeContext.Provider
        value={{
          state: trade,
          setState: noop,
        }}
      >
        <TradeProductsRefund
          conversation={conversation}
          itemsData={itemsData}
          trade={trade}
          reviewId={reviewId}
          lazyScrollContainer={lazyScrollContainer}
        />
        {products}
        {tagProducts}
        <TradeCancelInfo trade={trade} conversation={conversation} />
        <TradeDetail sendable={sendable} trade={trade} />
        {email && displaySensitive && (
          <DetailItem>
            <span>{t('domain.order.field.email')}</span>
            <span>{email}</span>
          </DetailItem>
        )}
        <TradeBuyerMemos trade={trade} />
        <TradeOfficialComment
          isShopeeIn30daysMemo={isShopeeIn30daysMemo}
          trade={trade}
          conversation={conversation}
          onEdit={() => setEditing(true)}
        />
        {editing ? (
          <TradeCommentForm initialValues={formInitialValues} onSubmit={handleSubmit}>
            {(form, props) => {
              const { isSubmitting, handleSubmit } = props;

              return (
                <MemoForm>
                  {form}
                  <section>
                    <Button size="small" onClick={() => setEditing(false)}>
                      {t('scene.chatWorkspace.orderTab.notes.action.cancelEdit')}
                    </Button>
                    <Button
                      size="small"
                      type="primary"
                      loading={isSubmitting}
                      onClick={() => handleSubmit()}
                    >
                      {t('scene.chatWorkspace.orderTab.notes.action.save')}
                    </Button>
                  </section>
                </MemoForm>
              );
            }}
          </TradeCommentForm>
        ) : null}
        {showCommentContent && (
          <TradeCommentContent
            trade={trade}
            conversation={conversation}
            onEdit={() => setEditing(true)}
          />
        )}
        {isYPHH ? <YiPinErpDetail /> : null}
      </TradeContext.Provider>
      <Toolbar>
        <div />
        {channel === 'WA_BIZ' ? null : (
          <Space className="flex-wrap justify-end">
            <ExportOrderButton trade={trade} conversation={conversation} />
            {isTFERPSupport && <TFERPPage trade={trade} />}
            <ContactWhatsapp
              orderPhone={phone}
              externalBuyerId={phone}
              region={conversation.region}
              channel={conversation.channel}
              externalStoreId={trade.externalShopId}
              buyerNick={buyerNickMask(conversation.buyerNick)}
              orderId={externalTradeId}
              storeName={conversation.storeName}
              talkPhone={conversation.phone}
            />
            {BtnDetailWrap}
            {isTiKTokUpperCase(channel) &&
              eligibility?.skuEligibility.some(({ lineItemEligibility }) =>
                lineItemEligibility.some(({ requestType }) => requestType === 'CANCEL'),
              ) && <CancelOrder order={trade} storeId={storeId} onUpdate={onUpdate} />}
            {isTiKTokUpperCase(channel) &&
              eligibility?.skuEligibility.some(({ lineItemEligibility }) =>
                lineItemEligibility.some(({ requestType }) => requestType === 'REFUND'),
              ) && <Refund type="REFUND" order={trade} storeId={storeId} onUpdate={onUpdate} />}
            {isTiKTokUpperCase(channel) &&
              eligibility?.skuEligibility.some(({ lineItemEligibility }) =>
                lineItemEligibility.some(({ requestType }) => requestType === 'RETURN_AND_REFUND'),
              ) && (
                <Refund
                  type="RETURN_AND_REFUND"
                  order={trade}
                  storeId={storeId}
                  onUpdate={onUpdate}
                />
              )}
            {supportSend ? (
              <Button size="small" type="primary" disabled={sendDisabled} onClick={sendHandler}>
                {t('scene.chatWorkspace.orderTab.action.send')}
              </Button>
            ) : null}
          </Space>
        )}
      </Toolbar>
    </Container>
  );
}

export default memo(TradeComponent);

const Container = styled.div`
  padding: 0 16px;
  background: var(--fett-bg-elevated);
  color: var(--fett-text-primary);
  > * {
    padding: 8px 0;
    border-bottom: solid 1px var(--fett-border-default);
  }
`;

const Top = styled.div`
  padding: 8px 0;
  > div {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 24px;

    > span {
      color: var(--fett-text-tertiary);
    }
  }
  > span {
    display: flex;
    align-items: center;
    height: 24px;
    font-size: 14px;
    color: var(--fett-text-secondary);
    > * {
      margin-left: 6px;
    }
  }
`;

const Product = styled(TradeProduct)`
  margin: 5px -5px;
`;

const MemoDetail = styled.div`
  border-bottom: none;
  > div {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 24px;
    font-size: 12px;
    color: var(--fett-text-secondary);
    button {
      font-size: 12px;
      padding-right: 0;
    }
  }
  > span {
    display: block;
    padding: 6px 12px;
    font-size: 12px;
    color: var(--fett-text-tertiary);
    background: var(--fett-bg-subtle);
    border-radius: 4px;
    word-break: break-all;
    white-space-collapse: preserve;
  }
`;

const OfficialMemo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: none;
  font-size: 12px;
  color: var(--fett-text-secondary);
  > span {
    flex: 0 0 auto;
    margin-right: 8px;
  }
  > div {
    padding: 6px 12px;
    font-size: 12px;
    color: var(--fett-text-tertiary);
    background: var(--fett-bg-subtle);
    border-radius: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const MemoForm = styled.div`
  textarea {
    &:hover,
    &:active,
    &:focus,
    &:visited {
      border-color: var(--main-color);
      box-shadow: none;
    }
  }
  > section {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-top: -15px;
    > * {
      margin-left: 10px;
    }
    .${({ theme }) => theme.getAntdPrefixedClassName('btn')} {
      &:hover,
      &:active,
      &:focus,
      &:visited {
        color: var(--main-color);
        border-color: var(--main-color);
      }
      &-primary {
        margin-left: 10px;
        &,
        &:hover,
        &:active,
        &:focus,
        &:visited {
          color: #fff;
          background: var(--main-color);
          border-color: var(--main-color);
        }
      }
    }
  }
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  > * {
    margin-left: 8px;
  }
  > {
    button,
    button:hover,
    button:active,
    button:focus,
    button:visited {
      margin-left: 10px;
      background: var(--main-color);
      border-color: var(--main-color);
    }
  }
  a:hover,
  a:active,
  a:focus,
  a:visited {
    color: var(--main-color);
    border-color: var(--main-color);
  }
`;

const breath = keyframes`
  0% {
    opacity: 0.2;
  }
  100% {
    opacity: 1;
  }
`;

const DetailBadge = styled(Badge)`
  sup {
    width: 10px;
    height: 10px;
    animation: ${breath} 1.2s linear 0.8s infinite alternate;
  }
`;

const SpanCOD = styled.span`
  color: var(--fett-text-tertiary);
  display: flex;
  b {
    font-weight: normal;
    max-width: 100px;
    display: inline-block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;
